<template>
  <div v-if="uiStore.getIsWelcomeScreen() || uiStore.getIsOnboarding()">
    <div v-if="uiStore.getIsWelcomeScreen()" class="welcome bg-[#111111]">
      <div v-if="!isVideoPlaying">
        <div>
          <div class="image-container" @click="handlePlayVideo">
            <video
              class="hero-preview"
              autoplay
              muted
              playsinline
              loop
              preload="metadata"
              :poster="slides[0].image">
              <source :src="slides[0].video" type="video/webm" />
            </video>
            <div
              class="flex items-center justify-center w-[40px] h-[40px] absolute bottom-[16px] left-[120px] bg-white rounded-full p-2">
              <vIcon name="welcomePlay" class="w-4 h-4" />
            </div>
          </div>

          <div
            class="mb-3 text-text-1 text-center text-[24px] font-[700] leading-[135%]"
            v-html="$t('other|onboarding|tma|welcome_screen_title')" />
          <div
            class="px-[40px] mb-3 text-text-light_gray text-center text-sm font-medium leading-[150%]"
            v-html="$t('other|onboarding|tma|welcome_screen_subtitle')" />
        </div>

        <BaseButton
          class="fixed bottom-[30px] left-[30px] right-[30px] ![--base-button-width-default:auto]"
          :class="{ 'button-animation': !isAnimationCompleted }"
          @click="handleNextButton">
          {{ $t("other|onboarding|tma|welcome_screen|button|learn_more") }}
        </BaseButton>
      </div>
      <WelcomeVideo v-else @close="handleCloseVideo" />
    </div>
    <Onboarding v-if="uiStore.getIsOnboarding()" />
  </div>
</template>

<script setup>
import { useUiStore } from "@/stores/ui";
import BaseButton from "@emcd-shared/base-button";
import vIcon from "@/components/ui/icon.vue";
import WelcomeVideo from "./WelcomeVideo.vue";
import Onboarding from "./Onboarding.vue";
import { ref, onMounted } from "vue";
import { watch, nextTick } from "vue";

const uiStore = useUiStore();

const isVideoPlaying = ref(false);
const isAnimationCompleted = ref(false);

const slides = [
  {
    title: "other|onboarding|p2p",
    subtitle: "other|onboarding|p2pDescription",
    // image: "/images/onboarding/1.png",
    video: "/images/onboarding/welcome.webm",
  },
];

const handlePlayVideo = () => {
  isVideoPlaying.value = true;
};

const handleCloseVideo = () => {
  const videoEl = document.querySelector(".hero-preview");
  if (videoEl && typeof videoEl.play === "function") {
    videoEl.play().catch(() => {});
  }
  isVideoPlaying.value = false;
};

const handleNextButton = () => {
  uiStore.setIsWelcomeScreen(false);
  uiStore.setIsOnboarding(true);
};

function springAnimate(element, { from, to, mass, stiffness, damping }) {
  return new Promise((resolve) => {
    let position = from;
    let velocity = 0;
    let lastTime = performance.now();
    const epsilonPosition = 0.5; // px
    const epsilonVelocity = 2; // px/s

    element.style.willChange = "transform";
    element.style.transform = `translate3d(0, ${position}px, 0)`;

    function frame(now) {
      const dt = Math.min((now - lastTime) / 1000, 0.032); // clamp dt for stability
      lastTime = now;

      const displacement = position - to;
      const springForce = -stiffness * displacement;
      const dampingForce = -damping * velocity;
      const acceleration = (springForce + dampingForce) / mass;

      velocity += acceleration * dt;
      position += velocity * dt;

      element.style.transform = `translate3d(0, ${position}px, 0)`;

      const isResting =
        Math.abs(position - to) < epsilonPosition && Math.abs(velocity) < epsilonVelocity;

      if (isResting) {
        element.style.transform = `translate3d(0, ${to}px, 0)`;
        element.style.willChange = "";
        resolve();
        return;
      }
      requestAnimationFrame(frame);
    }

    requestAnimationFrame(frame);
  });
}

async function runWelcomeAnimation() {
  isAnimationCompleted.value = false;
  await nextTick();
  const videoEl = document.querySelector(".hero-preview");
  if (videoEl && typeof videoEl.play === "function") {
    videoEl.play().catch(() => {});
  }
  const welcomeEl = document.querySelector(".welcome");
  if (welcomeEl) {
    const startOffset = Math.round(window.innerHeight * 0.1);
    await springAnimate(welcomeEl, {
      from: startOffset,
      to: 0,
      mass: 2,
      stiffness: 200,
      damping: 40,
    });
  }
  setTimeout(() => {
    isAnimationCompleted.value = true;
    if (videoEl && typeof videoEl.play === "function") {
      videoEl.play().catch(() => {});
    }
  }, 350);
}

onMounted(async () => {
  if (uiStore.getIsWelcomeScreen()) {
    await runWelcomeAnimation();
  }
});

watch(
  () => uiStore.getIsWelcomeScreen(),
  async (isOpen) => {
    if (isOpen) {
      await runWelcomeAnimation();
    }
  },
);
</script>

<style lang="scss" scoped>
.welcome {
  position: absolute;
  top: 0;
  left: 0;
  width: 100dvw;
  height: 100dvh;
  overflow: auto;
  z-index: 1000;
}

.image-container {
  margin: 20px auto 40px;
  width: 280px;
  height: 280px;
  border-radius: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  cursor: pointer;
}

.button-animation {
  animation: buttonAnimation 350ms ease-out both;
  animation-delay: 100ms;
}

@keyframes buttonAnimation {
  from {
    transform: scaleX(1.1);
  }

  to {
    transform: scaleX(1);
  }
}
</style>
